import React, { useCallback, useEffect, useState } from 'react';
import { Upload, FileText, IdCard, Check, FileSignature, ExternalLink } from 'lucide-react';
import uploadsService from '../../services/uploadsService';
import apiClient from '../../utils/apiClient';

/**
 * Customer documents. Two halves:
 *   READ  — signed/pending leases for every rental this customer holds, plus
 *           any manually uploaded photo ID / documents.
 *   WRITE — the photo-ID and document upload rows.
 *
 * Leases are read-only here on purpose: generate / send / void live on the
 * rental page (RentalLeaseSection) so there is one place that MUTATES a
 * lease. This tab is where you come to FIND a document — a lien file or a
 * dispute needs the lease for a specific unit, so rows carry the unit number.
 *
 * Edit-mode only — needs a persisted customerId to attach files to.
 */

const LEASE_BADGE = {
  signed: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  sent: 'bg-amber-50 text-amber-700 ring-amber-200',
  voided: 'bg-slate-100 text-slate-500 ring-slate-200',
};

const DocumentsSection = ({ customerId, facilityId }) => {
  const [photoStatus, setPhotoStatus] = useState(null);
  const [docStatus, setDocStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const [leases, setLeases] = useState([]);
  const [uploaded, setUploaded] = useState({ photo_id_url: '', document_urls: [] });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Unit number per lease, so a row reads "Unit 40" rather than an id. The
  // lease doc carries unit_id only; resolve once per distinct unit.
  const [unitNames, setUnitNames] = useState({});

  const load = useCallback(async () => {
    if (!customerId || !facilityId) return;
    setLoading(true);
    setLoadError(null);
    try {
      // Two independent fetches. They must NOT share a fate: the uploads call
      // was hitting /api/facilities/{id}/uploads/documents — a route that does
      // not exist (the real one is /api/uploads/documents with facility_id as
      // a query param) — so it 404'd, Promise.all rejected, and a SIGNED lease
      // that loaded perfectly well never rendered. "Not Found" for the whole
      // tab when only the uploads half failed. Settle them separately.
      const [leaseRes, docRes] = await Promise.allSettled([
        apiClient.get(`/api/facilities/${facilityId}/leases/`, {
          params: { customer_id: customerId },
        }),
        apiClient.get(`/api/uploads/documents`, {
          params: { facility_id: facilityId, customer_id: customerId },
        }),
      ]);

      if (leaseRes.status === 'fulfilled') {
        setLeases(leaseRes.value.data || []);
      } else {
        // A lease-load failure is the one worth surfacing — it is the primary
        // content of this tab.
        setLoadError(leaseRes.reason?.response?.data?.detail
          || 'Could not load leases');
        setLeases([]);
      }

      setUploaded(
        docRes.status === 'fulfilled'
          ? (docRes.value.data || { photo_id_url: '', document_urls: [] })
          : { photo_id_url: '', document_urls: [] },
      );

      const rows = leaseRes.status === 'fulfilled' ? (leaseRes.value.data || []) : [];

      const ids = [...new Set(rows.map((l) => l.unit_id).filter(Boolean))];
      const names = {};
      await Promise.all(
        ids.map(async (uid) => {
          try {
            const u = await apiClient.get(`/api/facilities/${facilityId}/units/${uid}`);
            names[uid] = u.data?.unit_number;
          } catch {
            /* a deleted unit just renders without a number */
          }
        }),
      );
      setUnitNames(names);
    } catch (e) {
      setLoadError(e?.response?.data?.detail || 'Could not load documents');
    } finally {
      setLoading(false);
    }
  }, [customerId, facilityId]);

  useEffect(() => {
    load();
  }, [load]);

  // Same blob-open pattern as RentalLeaseSection — the PDF route is
  // authenticated, so a bare href would 401.
  const openLeasePdf = async (leaseId) => {
    try {
      const r = await apiClient.get(
        `/api/facilities/${facilityId}/leases/${leaseId}/pdf`,
        { responseType: 'blob' },
      );
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setLoadError(e?.response?.data?.detail || 'Could not load PDF');
    }
  };

  const openUpload = async (fileUrl) => {
    try {
      const r = await apiClient.get(fileUrl, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setLoadError(e?.response?.data?.detail || 'Could not open file');
    }
  };

  const handlePhotoId = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setPhotoStatus(null);
    try {
      const res = await uploadsService.uploadPhotoId(customerId, file);
      setPhotoStatus({ ok: true, msg: `Uploaded (${res.data.file_size_mb} MB)` });
      load();
    } catch (err) {
      setPhotoStatus({ ok: false, msg: err.response?.data?.detail || 'Upload failed' });
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  };

  const handleDocument = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setDocStatus(null);
    try {
      const res = await uploadsService.uploadDocument(customerId, file);
      setDocStatus({ ok: true, msg: `Uploaded (${res.data.file_size_mb} MB)` });
      load();
    } catch (err) {
      setDocStatus({ ok: false, msg: err.response?.data?.detail || 'Upload failed' });
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  };

  const Row = ({ icon: Icon, title, hint, onChange, status }) => (
    <div className="rounded-xl border border-slate-200/80 p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-4 w-4 text-slate-400" />
        <span className="text-[13px] font-medium text-slate-700">{title}</span>
      </div>
      <p className="text-[12px] text-slate-400 mb-3">{hint}</p>
      <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-[13px] font-medium text-white hover:bg-blue-700 cursor-pointer transition-colors">
        <Upload className="h-3.5 w-3.5" />
        Choose file
        <input
          type="file"
          className="hidden"
          disabled={busy}
          onChange={onChange}
          accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.heic"
        />
      </label>
      {status && (
        <p className={`mt-2 text-[12px] flex items-center gap-1 ${status.ok ? 'text-emerald-600' : 'text-red-500'}`}>
          {status.ok && <Check className="h-3 w-3" />}
          {status.msg}
        </p>
      )}
    </div>
  );

  const fileLabel = (url) => {
    try {
      return decodeURIComponent(String(url).split('/').pop()) || 'Document';
    } catch {
      return 'Document';
    }
  };

  const docUrls = uploaded.document_urls || [];
  const hasAnything = leases.length > 0 || uploaded.photo_id_url || docUrls.length > 0;

  return (
    <div className="space-y-3">
      {loadError && (
        <p className="text-[12px] text-red-500">{loadError}</p>
      )}

      {/* ---- on file (read) ---- */}
      <div className="rounded-xl border border-slate-200/80 p-4">
        <div className="flex items-center gap-2 mb-3">
          <FileText className="h-4 w-4 text-slate-400" />
          <span className="text-[13px] font-medium text-slate-700">On file</span>
        </div>

        {loading ? (
          <p className="text-[12px] text-slate-400">Loading…</p>
        ) : !hasAnything ? (
          <p className="text-[12px] text-slate-400">
            No documents yet. Signed leases appear here automatically.
          </p>
        ) : (
          <div className="space-y-1.5">
            {leases.map((l) => (
              <div
                key={l._id}
                className="flex items-center justify-between gap-3 py-1.5 border-b border-slate-100 last:border-0"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <FileSignature className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                  <span className="text-[13px] text-slate-700 truncate">
                    Lease
                    {unitNames[l.unit_id] ? ` — Unit ${unitNames[l.unit_id]}` : ''}
                  </span>
                  <span
                    className={`text-[11px] px-1.5 py-0.5 rounded ring-1 shrink-0 ${
                      LEASE_BADGE[l.status] || 'bg-slate-100 text-slate-500 ring-slate-200'
                    }`}
                  >
                    {l.status}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => openLeasePdf(l._id)}
                  className="text-[12px] text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1 shrink-0"
                >
                  <ExternalLink className="h-3 w-3" />
                  View
                </button>
              </div>
            ))}

            {uploaded.photo_id_url && (
              <div className="flex items-center justify-between gap-3 py-1.5 border-b border-slate-100 last:border-0">
                <div className="flex items-center gap-2 min-w-0">
                  <IdCard className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                  <span className="text-[13px] text-slate-700 truncate">Photo ID</span>
                </div>
                <button
                  type="button"
                  onClick={() => openUpload(uploaded.photo_id_url)}
                  className="text-[12px] text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1 shrink-0"
                >
                  <ExternalLink className="h-3 w-3" />
                  View
                </button>
              </div>
            )}

            {docUrls.map((url) => (
              <div
                key={url}
                className="flex items-center justify-between gap-3 py-1.5 border-b border-slate-100 last:border-0"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                  <span className="text-[13px] text-slate-700 truncate">{fileLabel(url)}</span>
                </div>
                <button
                  type="button"
                  onClick={() => openUpload(url)}
                  className="text-[12px] text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1 shrink-0"
                >
                  <ExternalLink className="h-3 w-3" />
                  View
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ---- upload (write) ---- */}
      <Row
        icon={IdCard}
        title="Photo ID"
        hint="Driver's license or government ID. Replaces any existing photo ID."
        onChange={handlePhotoId}
        status={photoStatus}
      />
      <Row
        icon={FileText}
        title="Document"
        hint="Insurance, a lease the tenant signed with a previous owner, or any other supporting document. Leases signed in mySMS360 arrive here on their own."
        onChange={handleDocument}
        status={docStatus}
      />
    </div>
  );
};

export default DocumentsSection;
